import React from "react";
import { Navigate } from "react-router-dom";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { Link as RouterLink } from "react-router-dom";
import { C } from "../../common/theme";
import { useAuth, PageKey } from "../../common/auth";
import { ROUTES } from "../../common/constant/routes";

interface ProtectedRouteProps {
  children: React.ReactNode;
  /** Page key(s) that grant access; with several, any one is enough.
   * Omit if any authenticated admin can view. */
  requiredPermission?: PageKey | PageKey[];
  /** If true, only superadmins can access this route. */
  superadminOnly?: boolean;
}

function NoAccess({ message }: { message: string }) {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", textAlign: "center", minHeight: "60vh", gap: 1.25, px: 2 }}>
      <Box sx={{ width: 48, height: 48, borderRadius: "10px", display: "grid", placeItems: "center", bgcolor: C.bone, color: C.coralText }}>
        <LockOutlinedIcon />
      </Box>
      <Typography variant="h5" component="h1">
        No access to this page
      </Typography>
      <Typography variant="body2" sx={{ color: C.muted, maxWidth: 380 }}>
        {message}
      </Typography>
      <Button component={RouterLink} to={ROUTES.OVERVIEW} variant="outlined" sx={{ mt: 1 }}>
        Back to Overview
      </Button>
    </Box>
  );
}

export default function ProtectedRoute({
  children,
  requiredPermission,
  superadminOnly = false,
}: ProtectedRouteProps) {
  const { adminUser, loading, firebaseUser, idToken } = useAuth();

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <CircularProgress size={28} sx={{ color: C.ink }} />
      </Box>
    );
  }

  // Not logged in at all
  if (!firebaseUser || !idToken) {
    return <Navigate to={ROUTES.LOGIN} replace />;
  }

  // Logged into Firebase but not an admin user
  if (!adminUser) {
    return <Navigate to={ROUTES.LOGIN} replace />;
  }

  // Superadmin-only route check
  if (superadminOnly && adminUser.role !== "superadmin") {
    return <NoAccess message="Only superadmins can open this page. Ask a superadmin if you need it." />;
  }

  // Page-level permission check
  if (requiredPermission) {
    const anyOf = Array.isArray(requiredPermission) ? requiredPermission : [requiredPermission];
    const hasAccess =
      adminUser.role === "superadmin" ||
      anyOf.some((key) => (adminUser.permissions ?? []).includes(key));
    if (!hasAccess) {
      return <NoAccess message="Your admin account doesn't include this page. Ask a superadmin to add it." />;
    }
  }

  return <>{children}</>;
}
